import { useMemo, useState } from "react";
import { X, Trash2, MapPin, ShoppingBag, Calendar, ChevronRight, Star, Sparkles, Droplets, Layers, Plus, Zap, Waves, Sun, Hand } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import InlineBookingFlow from "@/components/InlineBookingFlow";
import { LoyaltyCartSummary } from "@/components/LoyaltyPointsBadge";
import CartPendingHoldChip from "@/components/CartPendingHoldChip";
import SmartPairSuggestion from "@/components/SmartPairSuggestion";
import AfterpayInline from "@/components/AfterpayInline";

const CLINIC_LOCATIONS = [
  { id: "1", name: "Carleton Place", address: "163 Bridge Street, Downtown, Carleton Place, ON" },
  { id: "2", name: "Kanata", address: "320 March Road, Sixth Floor, Clinic 603, Kanata, ON" },
  { id: "3", name: "Ottawa", address: "1390 Prince of Wales Drive, Main Floor, Ottawa, ON" },
];

// ── QUICK ADD-ONS ── Sept 6 2026 · Karine ask
// Priced as bundle-only discounts (regular menu prices in parens for the
// tiny "was" strikethrough). Only show these when the cart already has
// at least one full-price treatment.
const CART_QUICK_ADDONS = [
  { id: "addon-hydrafacial",
    name: "HydraFacial (Crown Signature)",
    price: "$79",  wasPrice: "$132",
    category: "Add-on · 30 min",
    icon: Droplets, tone: "cyan" },
  { id: "addon-lymphatic-drain",
    name: "Lymphatic Facial Drainage",
    price: "$49",  wasPrice: "$99",
    category: "Add-on · 20 min · Post-injection recovery",
    icon: Waves, tone: "teal" },
  { id: "addon-laser-genesis",
    name: "Laser Genesis (Face)",
    price: "$99",  wasPrice: "$200",
    category: "Add-on · Skin rejuvenation",
    icon: Zap, tone: "amber" },
  { id: "addon-express-glow",
    name: "Éminence Express Glow Facial",
    price: "$69",  wasPrice: null,
    category: "Add-on · 30 min · Botanical glow",
    icon: Sun, tone: "emerald" },
  // Sept 9 2026 · Karine ask — offer Radiesse Hands as a "second area"
  // upsell in the cart. Face-only filter is applied at render time so
  // this chip only surfaces when the cart already has a face treatment.
  { id: "addon-radiesse-hands",
    name: "Radiesse® — Hands (add a second area)",
    price: "$750",  wasPrice: "$750",
    category: "Add-on · 30 min · Karine · biostimulator for hands",
    icon: Hand, tone: "rose",
    // Contextual: only offered when the cart contains a face-area treatment.
    requiresFaceItem: true,
    // Matches the exact Square catalog name so InlineBookingFlow picks
    // the right variation when the client hits checkout.
    squareName: "Radiesse® — Hands (hyperdilute)" },
];

// Face-area keywords used to gate the Radiesse Hands add-on. If ANY
// cart item's name/category contains one of these, the hands upsell
// is offered. Kept broad on purpose — we'd rather over-offer than hide.
const FACE_KEYWORDS = [
  "face","botox","tox","filler","juvederm","restylane","sculptra","radiesse",
  "cheek","chin","jaw","nasolabial","lip","lift","potenza","microneedling",
  "hydrafacial","facial","peel","glow","redensity","skin booster","eminence",
  "aveda","laser genesis","photofacial","prp","vampire","nefertiti","hollywood",
];
const cartHasFaceItem = (items) => (items || []).some((c) => {
  const blob = `${c.name || ""} ${c.category || ""} ${c.type || ""}`.toLowerCase();
  return FACE_KEYWORDS.some((k) => blob.includes(k));
});

const ADDON_TONE = {
  cyan:    "bg-cyan-50 border-cyan-200 text-cyan-700",
  teal:    "bg-teal-50 border-teal-200 text-teal-700",
  amber:   "bg-amber-50 border-amber-200 text-amber-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  rose:    "bg-rose-50 border-rose-200 text-rose-700",
};

// Cross-sell treatments shown inside the cart so clients can stack more before booking
const CART_CROSS_SELL = [
  { key: "botox",         name: "Botox",                 path: "/botox-ottawa",          icon: Sparkles, accent: "amber"   },
  { key: "fillers",       name: "Dermal Fillers",        path: "/dermal-fillers",        icon: Droplets, accent: "rose"    },
  { key: "skin-boosters", name: "Skin Boosters",         path: "/skin-boosters",         icon: Layers,   accent: "pink"    },
  { key: "sculptra",      name: "Sculptra",              path: "/sculptra",              icon: Plus,     accent: "purple"  },
  { key: "microneedling", name: "Medical Microneedling", path: "/medical-microneedling", icon: Zap,      accent: "emerald" },
];

const ACCENT_BG = {
  amber:   "bg-amber-50 border-amber-200 hover:border-amber-400 text-amber-700",
  rose:    "bg-rose-50 border-rose-200 hover:border-rose-400 text-rose-700",
  pink:    "bg-pink-50 border-pink-200 hover:border-pink-400 text-pink-700",
  purple:  "bg-purple-50 border-purple-200 hover:border-purple-400 text-purple-700",
  emerald: "bg-emerald-50 border-emerald-200 hover:border-emerald-400 text-emerald-700",
};

const BookingCart = ({ locations: externalLocations }) => {
  const locations = externalLocations?.length ? externalLocations : CLINIC_LOCATIONS;
  const {
    cartItems,
    addToCart,
    removeFromCart,
    clearCart,
    incrementQty,
    decrementQty,
    isCartOpen,
    setIsCartOpen,
    selectedLocation,
    setSelectedLocation,
  } = useCart();

  const [showBookingFlow, setShowBookingFlow] = useState(false);
  const [showCrossSell, setShowCrossSell] = useState(false);

  // Memoise the prefill package so it has a STABLE reference between
  // renders. Without this, every state change inside the booking flow
  // re-renders BookingCart → new prefillPackage object → re-fires the
  // catalog-fetch effect in InlineBookingFlow → cancels the in-flight
  // fetch BEFORE the auto-match runs → user gets stuck at the treatment
  // step with the Next button greyed out and has to manually pick the
  // treatment from the list.
  //
  // Field-name compatibility: different add-to-cart sites use slightly
  // different keys. Older promo components (e.g. SummerFillerEvent) save
  // `service_name` / `service_type`; newer ones use `name` / `type`.
  // InlineBookingFlow's auto-matcher reads `name` / `type`, so when a
  // cart item only has the `service_*` variant the matcher silently fell
  // through and the visitor was forced to pick the treatment a SECOND
  // time. We normalise both shapes here.
  const firstCartItem = cartItems?.[0];
  const prefillPackage = useMemo(() => {
    if (!firstCartItem) return null;
    const numeric = parseFloat(
      String(firstCartItem.price || "").replace(/[$,\s]/g, ""),
    );
    const name = firstCartItem.name || firstCartItem.service_name || "";
    const type = firstCartItem.type || firstCartItem.service_type || "";
    const category = firstCartItem.category || firstCartItem.service_type || "";
    return {
      name,
      category,
      type,
      // Pass `serviceType` too — InlineBookingFlow's matcher checks both
      // `packageInfo.serviceType` and `packageInfo.type` (see line 490).
      serviceType: type,
      priceOverride: Number.isFinite(numeric) ? numeric : null,
      priceLabel: firstCartItem.price,
      duration: firstCartItem.duration,
      // === UNIVERSAL FAST BOOKING (2026-02) =========================
      // If the cart item ships a Square variation/catalog id or a
      // canonical exact name, InlineBookingFlow will auto-select the
      // matching treatment on step 1 and skip the picker entirely.
      // Every add-to-cart callsite that provides ANY of these ids will
      // benefit — no keyword hacks required.
      variation_id: firstCartItem.variation_id || firstCartItem.variationId || null,
      catalog_id: firstCartItem.catalog_id || firstCartItem.catalogId || firstCartItem.catalog_object_id || null,
      exact_name: firstCartItem.exact_name || firstCartItem.exactName || firstCartItem.square_name || null,
      // ==============================================================
      // Strongest possible hint: a phrase from the canonical Square service
      // name (e.g. "1 full syringe filler"). Lets InlineBookingFlow pick
      // the correct variation even when multiple services share the same
      // duration bucket (e.g. seasonal promos that are also 30 min).
      variationHint: firstCartItem.variationHint || firstCartItem.variation_hint || null,
      // Carry promotion + bonus tags forward so InlineBookingFlow can
      // append them to the booking notes the way it does for normal
      // package add-ons.
      promotion: firstCartItem.promotion,
      bonus: firstCartItem.bonus,
      syringes: firstCartItem.syringes,
    };
  }, [
    firstCartItem?.name,
    firstCartItem?.service_name,
    firstCartItem?.category,
    firstCartItem?.type,
    firstCartItem?.service_type,
    firstCartItem?.price,
    firstCartItem?.duration,
    firstCartItem?.variation_id,
    firstCartItem?.variationId,
    firstCartItem?.catalog_id,
    firstCartItem?.catalogId,
    firstCartItem?.catalog_object_id,
    firstCartItem?.exact_name,
    firstCartItem?.exactName,
    firstCartItem?.square_name,
    firstCartItem?.variationHint,
    firstCartItem?.variation_hint,
    firstCartItem?.promotion,
    firstCartItem?.bonus,
    firstCartItem?.syringes,
  ]);

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-[9998] backdrop-blur-sm"
        onClick={() => setIsCartOpen(false)}
      />

      {/* Cart Sidebar — full screen on mobile, sidebar on desktop */}
      <div
        className="fixed inset-0 sm:inset-auto sm:right-0 sm:top-0 sm:h-full sm:w-full sm:max-w-md bg-white z-[9999] flex flex-col animate-slide-in-right shadow-2xl"
        data-testid="booking-cart"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-black rounded-full flex items-center justify-center">
              <ShoppingBag className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Your Cart
              </h2>
              <p className="text-xs text-gray-500">{cartItems.length} treatment{cartItems.length !== 1 ? 's' : ''} selected</p>
            </div>
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="p-2.5 hover:bg-gray-200 rounded-full transition-colors"
            data-testid="close-cart"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {showBookingFlow ? (
            <div className="p-4">
              <InlineBookingFlow
                isOpen={true}
                onClose={() => {
                  setShowBookingFlow(false);
                  clearCart();
                  setIsCartOpen(false);
                }}
                // Pass the user's selections from the cart drawer so the
                // modal can SKIP its own location + treatment steps and land
                // the client directly on date/time selection. Previously the
                // user had to re-pick the location and re-pick the treatment,
                // making it a 5+ step flow with duplicate selections.
                prefillLocationId={selectedLocation?.id}
                prefillPackage={prefillPackage}
              />
            </div>
          ) : cartItems.length === 0 ? (
            <div className="text-center py-16 px-6">
              <ShoppingBag className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No treatments selected</p>
              <p className="text-sm text-gray-400 mt-2">
                Browse our services and tap "Add to Cart" to get started
              </p>
            </div>
          ) : (
            <div className="p-5 space-y-6">
              {/* 15-min point-hold countdown — only renders if signed-in client has an active hold */}
              <CartPendingHoldChip />

              {/* Smart medically-vetted pairing suggestion (Botox+Filler, Microneedling+PRP, etc.) */}
              <SmartPairSuggestion />

              {/* Cart Items */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                  Your Treatments
                </h3>
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-start gap-3"
                      data-testid={`cart-item-${item.id}`}
                    >
                      <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <ShoppingBag className="w-5 h-5 text-rose-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 text-sm leading-tight">{item.name}</h4>
                        {item.category && <p className="text-xs text-gray-500 mt-0.5">{item.category}</p>}
                        <p className="text-base font-bold text-rose-600 mt-1">{item.price}</p>
                        <AfterpayInline price={item.price} size="xs" tone="light" className="mt-1" />
                        {/* Quantity stepper — Sept 6 2026 · Karine ask.
                            Only shown for stackable items (add-ons). Regular
                            treatments hide the stepper since they dedup. */}
                        {(item.qty || 1) > 1 || item.category === "Add-on" ? (
                          <div className="mt-2 inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full px-1 py-0.5"
                               data-testid={`cart-qty-stepper-${item.id}`}>
                            <button
                              onClick={() => decrementQty(item.id)}
                              className="w-6 h-6 rounded-full bg-gray-50 hover:bg-red-50 text-gray-600 hover:text-red-600 text-sm font-bold transition"
                              data-testid={`cart-qty-decrement-${item.id}`}
                              aria-label="Decrease quantity"
                            >
                              −
                            </button>
                            <span className="min-w-[24px] text-center text-sm font-bold tabular-nums"
                                  data-testid={`cart-qty-value-${item.id}`}>
                              {item.qty || 1}
                            </span>
                            <button
                              onClick={() => incrementQty(item.id)}
                              className="w-6 h-6 rounded-full bg-gray-50 hover:bg-green-50 text-gray-600 hover:text-green-700 text-sm font-bold transition"
                              data-testid={`cart-qty-increment-${item.id}`}
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── QUICK ADD-ONS ── Sept 6 2026 · Karine ask
                  Bundle-priced adjuncts clients can tack onto their visit
                  in one tap. Hidden when cart is empty (renders inside the
                  else-branch already). Adds fire the same addToCart hook
                  so the app-wide cart context is the single source of truth. */}
              <div data-testid="cart-quick-addons">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" />
                  Add to your visit
                </h3>
                <p className="text-[11px] text-gray-500 mb-3 leading-snug">
                  Bundle-only pricing when you book alongside your treatment.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {CART_QUICK_ADDONS
                    .filter((a) => !a.requiresFaceItem || cartHasFaceItem(cartItems))
                    .map((a) => {
                    const existing = cartItems.find((c) => c.id === a.id);
                    const qty = existing?.qty || 0;
                    const Icon = a.icon;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() =>
                          addToCart({
                            id: a.id,
                            name: a.name,
                            price: a.price,
                            category: a.category,
                            source: "quick-addon",
                            squareName: a.squareName,
                          })
                        }
                        className={`text-left p-3 rounded-xl border-2 transition-all ${
                          qty > 0
                            ? "bg-green-50 border-green-400 hover:bg-green-100 hover:-translate-y-0.5"
                            : `${ADDON_TONE[a.tone]} hover:shadow-md hover:-translate-y-0.5`
                        }`}
                        data-testid={`quick-addon-${a.id}`}
                      >
                        <div className="flex items-start gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-white/70 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold leading-tight text-gray-900">{a.name}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">{a.category}</p>
                            <div className="mt-1.5 flex items-baseline gap-1.5">
                              <span className="text-sm font-bold">{a.price}</span>
                              {a.wasPrice && a.wasPrice !== a.price && (
                                <span className="text-[10px] text-gray-400 line-through">{a.wasPrice}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {qty > 0 ? (
                              <span className="text-[11px] font-bold text-green-700 uppercase">×{qty}</span>
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center border border-current">
                                <Plus className="w-3.5 h-3.5" />
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200" />

              {/* Location Selection */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  Choose Location
                </h3>
                <div className="space-y-2.5">
                  {locations.map((location) => (
                    <button
                      key={location.id}
                      onClick={() => setSelectedLocation(location)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        selectedLocation?.id === location.id
                          ? "border-rose-500 bg-rose-50 shadow-sm"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                      data-testid={`location-${location.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{location.name}</h4>
                          <p className="text-xs text-gray-500 mt-0.5">{location.address}</p>
                        </div>
                        {selectedLocation?.id === location.id && (
                          <div className="w-6 h-6 bg-rose-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <ChevronRight className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Cross-sell — "Want to look at other treatments?" */}
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-gradient-to-br from-amber-50/50 to-rose-50/30">
                <button
                  type="button"
                  onClick={() => setShowCrossSell((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-amber-50/60 transition-colors"
                  data-testid="cart-crosssell-toggle"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-sm font-semibold text-gray-900 leading-tight">
                        Want to look at other treatments?
                      </p>
                      <p className="text-[11px] text-gray-500">Add more before you book</p>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-amber-500 flex-shrink-0 transition-transform ${showCrossSell ? "rotate-90" : ""}`} />
                </button>
                {showCrossSell && (
                  <div className="px-4 pb-4 pt-1 grid grid-cols-2 gap-2" data-testid="cart-crosssell-list">
                    {CART_CROSS_SELL.map((t) => {
                      const Icon = t.icon;
                      return (
                        <a
                          key={t.key}
                          href={t.path}
                          onClick={() => setIsCartOpen(false)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-xs font-semibold transition-all ${ACCENT_BG[t.accent]}`}
                          data-testid={`cart-crosssell-${t.key}`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{t.name}</span>
                          <ChevronRight className="w-3.5 h-3.5 ml-auto flex-shrink-0 opacity-60" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Clear Cart */}
              <div className="text-center pt-2">
                <button
                  onClick={clearCart}
                  className="text-xs text-gray-400 hover:text-red-500 underline transition-colors"
                  data-testid="clear-cart"
                >
                  Clear all selections
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        {cartItems.length > 0 && !showBookingFlow && (
          <div className="flex-shrink-0 p-5 border-t border-gray-200 bg-white safe-area-bottom">
            <Button
              onClick={() => setShowBookingFlow(true)}
              disabled={!selectedLocation}
              className={`w-full py-6 text-base font-semibold rounded-xl ${
                selectedLocation 
                  ? 'bg-black hover:bg-gray-800 text-white' 
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
              data-testid="book-now-btn"
            >
              {selectedLocation ? (
                <>
                  <Calendar className="w-5 h-5 mr-2" />
                  Book at {selectedLocation.name}
                </>
              ) : (
                "Select a location above"
              )}
            </Button>
            <p className="text-[11px] text-center text-gray-400 mt-2">
              Secure direct booking — choose your date & time
            </p>
            <LoyaltyCartSummary cartItems={cartItems} />
          </div>
        )}
      </div>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.25s ease-out;
        }
        .safe-area-bottom {
          padding-bottom: max(1.25rem, env(safe-area-inset-bottom));
        }
      `}</style>
    </>
  );
};

export default BookingCart;
